import moment from "moment";
import { mapState } from "vuex";

import { countByKeys } from "@/utils/countByKeys";
import { toTranslationKey, cutString } from "@/utils/stringFormat";
import { PremiumServiceEventTypeEnum, PremiumServiceEventTypes, PremiumServiceProductTypeEnum, PremiumServiceRequestTypeEnum } from "@/enums/PremiumServiceEventTypes";
import { ServiceStatus } from "@/enums/ServiceStatus";

export default {
  data() {
    return {
      eventTypes: Object.values(PremiumServiceEventTypeEnum),
      requestTypes: Object.values(PremiumServiceRequestTypeEnum),
      productTypes: Object.values(PremiumServiceProductTypeEnum),
      loading: true,
      headers: [],
      jsonFields: {
        Service: "name",
        Type: "serviceItemType",
        Price: "cost",
        Provider: "providerName",
        Dimension: "category"
      },
      priceFilter: '',
      showAllProviders: false,
      showAllDimensions: false,
      topN: 10
    };
  },
  async created() {
    try {
      this.headers = [
        { text: this.$t('GeneralTranslations.Service'), align: "start", value: "name" },
        { text: this.$t('GeneralTranslations.Type'), value: "serviceItemType" },
        {
          text: this.$t('One off Cost '),
          value: "oneOffCost",
          filter: (value: any, search: string, item: any) => {
            if (!search) return true;
            const display = this.getOneOffDisplay(item);
            const normalizedDisplay = display.toLowerCase()
              .replace(/€/g, 'euro')
              .replace(/\$/g, 'dollar')
              .replace(/£/g, 'pound');
            const normalizedSearch = search.toLowerCase()
              .replace(/€/g, 'euro')
              .replace(/\$/g, 'dollar')
              .replace(/£/g, 'pound');
            return normalizedDisplay.includes(normalizedSearch);
          }
        },
        { text: this.$t('GeneralTranslations.Provider'), value: "providerName" },
        { text: this.$t('GeneralTranslations.Dimension'), value: "category" },
      ]
      await this.$store.dispatch("getAdditionalServices");
      await this.$store.dispatch("getServiceRequests");
      await this.$store.dispatch("getFacility");

     
   
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  },
  computed: {
    ...mapState({
      facility: (state) => (state as any).facilityModule.selectedFacility,
      residents: state => (state as any).residentModule.residentsShortData,
      requests: state => (state as any).additionalServicesModule.serviceRequests
    }),
    services() {
      return this.$store.state.additionalServicesModule.additionalServices.map((s: any) => {

        const service = s

        if (this.eventTypes.includes(s.type)) {
          service.serviceItemType = PremiumServiceEventTypes.Event
        }
        else if (this.requestTypes.includes(s.type)) {
          service.serviceItemType = PremiumServiceEventTypes.Request
        }
        else if (this.productTypes.includes(s.type)) {
          service.serviceItemType = PremiumServiceEventTypes.Product
        }

        return service
      })
    },
    conciergeServices() {
      const requests = this.$store.state.additionalServicesModule.serviceRequests
        .filter(x => [
          PremiumServiceEventTypes.AppAccess,
          PremiumServiceEventTypes.ByAppointment,
          PremiumServiceEventTypes.Request,
          PremiumServiceEventTypes.Event,
          PremiumServiceEventTypes.EventBased,
          PremiumServiceEventTypes.ConciergeEvent,
          PremiumServiceEventTypes.Gift,
          PremiumServiceEventTypes.Product
        ].includes(x.serviceItemType));
      return requests
    },
    conciergeEvents() {
      return this.services.filter((x: any) => x.serviceItemType == PremiumServiceEventTypes.Event)
    },
    conciergeRequests() {
      return this.services.filter((x: any) => x.serviceItemType == PremiumServiceEventTypes.Request)
    },
    conciergeProducts() {
      return this.services.filter((x: any) => x.serviceItemType == PremiumServiceEventTypes.Product)
    },
    requestsWithServices() {
      return this.requests.map(request => {
        const service = this.services.find(
          service => service.id === request.serviceId
        );

        return {
          request,
          service
        };
      });
    },
    premiumServiceReport() {
      return this.services;
    },
    residentsWithMostsServices() {
      const residentsWithServices = this.residents.map(resident => {
        const requests = this.conciergeServices.filter(x => x.residentId === resident.id && [ServiceStatus.Completed, ServiceStatus.Approved, ServiceStatus.AwaitingApproval].includes(x.status))

        const item = {
          id: resident.id,
          name: resident?.name || resident?.pname,
          conciergeRequestsCount: requests.length,
          imageUrl: resident?.imageUrl,
        }

        return item;
      }).sort((left, right) => right.conciergeRequestsCount - left.conciergeRequestsCount).slice(0, 6);

      return residentsWithServices;
    },
    servicesByProvider() {
      return countByKeys(this.services, "providerName");
    },
    servicesByCategory() {
      return countByKeys(this.services, "category");
    },
    topServicesByProvider() {
      if (this.showAllProviders) {
        return this.servicesByProvider;
      }
      
      // Convert to array, sort by count descending, take top N, convert back to object
      const entries = Object.entries(this.servicesByProvider)
        .sort((a: any, b: any) => b[1] - a[1])
        .slice(0, this.topN);
      
      return Object.fromEntries(entries);
    },
    topServicesByCategory() {
      if (this.showAllDimensions) {
        return this.servicesByCategory;
      }
      
      // Convert to array, sort by count descending, take top N, convert back to object
      const entries = Object.entries(this.servicesByCategory)
        .sort((a: any, b: any) => b[1] - a[1])
        .slice(0, this.topN);
      
      return Object.fromEntries(entries);
    },
    servicesByChargeType() {
      return countByKeys(this.services, "serviceType");
    },
    serviceRequestWithBookedBy() {
      return this.requests.map(request => {
        let bookedBy;

        if (request.bookByFamilyName) {
          bookedBy = this.$t('GeneralTranslations.Family');
        } else if (request.bookByStaffName) {
          bookedBy = this.$t('GeneralTranslations.Staff');
        } else {
          bookedBy = this.$t('GeneralTranslations.Resident');
        }

        return {
          ...request,
          bookedBy
        };
      });
    },
    servicesByBookedBy() {
      return countByKeys(this.serviceRequestWithBookedBy, "bookedBy", { skipEmpty: true });
    },
    servicesByDays() {
      const sortedRequest = this.requests.sort((left, right) =>
        moment.utc(left.createdDateTime).diff(moment.utc(right.createdDateTime))
      );

      const formattedRequests = sortedRequest.map(request => ({
        ...request,
        createdDateTime: moment(request.createdDateTime).format("DD/MM")
      }));
      return countByKeys(formattedRequests, "createdDateTime");
    },
    facilityCurrency() {
      return this.$store.getters.facilityCurrency;
    }
  },
  methods: {
    getOneOffDisplay(item: any) {
      if (!item) return '';
      if (item.oneOffCost === 'POA') return 'POA';
      if (item.oneOffCost === 'Free') return 'Free';
      if (item.oneOffCost) return `${this.facilityCurrency}${item.oneOffCost}`;
      return '';
    },
    getSubscriptionDisplay(item: any) {
      if (!item) return '';
      if (item.subscriptionCost === 'Free') return 'Free';
      if (item.subscriptionCost) return `${this.facilityCurrency} ${item.subscriptionCost}`;
      return '';
    },
    priceCustomSort(items: any[], sortBy: string[], sortDesc: boolean[]): any[] {
      if (!sortBy || !sortBy.length) return items;
      const key = sortBy[0];
      const desc = !!sortDesc[0];

      const getOrderRank = (item: any): { group: number; value: number } => {
        let display = '';
        if (key === 'oneOffCost') {
          display = this.getOneOffDisplay(item) || '';
        } else if (key === 'subscriptionCost') {
          display = this.getSubscriptionDisplay(item) || '';
        } else {
          display = this.getPrice(item) || '';
        }

        const lower = display.toLowerCase();
        if (!display) return { group: 3, value: Number.NEGATIVE_INFINITY };
        if (lower === 'free') return { group: 2, value: Number.NEGATIVE_INFINITY };
        if (lower === 'poa') return { group: 1, value: Number.NEGATIVE_INFINITY };

        const m = display.match(/[\d.]+/);
        const num = m ? parseFloat(m[0]) : Number.NaN;
        if (!isNaN(num)) return { group: 0, value: num };
        return { group: 3, value: Number.NEGATIVE_INFINITY };
      };

      const compare = (a: any, b: any): number => {
        if (key !== 'oneOffCost' && key !== 'subscriptionCost') {
          const av = (a as any)[key];
          const bv = (b as any)[key];
          if (av === bv) return 0;
          if (desc) return av < bv ? 1 : -1;
          return av > bv ? 1 : -1;
        }

        const ra = getOrderRank(a);
        const rb = getOrderRank(b);

        if (ra.group !== rb.group) return ra.group - rb.group;

        if (ra.group === 0) {
          return desc ? rb.value - ra.value : ra.value - rb.value;
        }

        return 0;
      };

      return items.slice().sort(compare);
    },
    routeToLConciergeServiceReportByResidents() {
      this.$router.push("/reports/premium-service-reports/residents");
    },
    convertToTranslationKey(text: string) {
      return toTranslationKey(text)
    },
    getPrice(item: any) {
      if (!item) return "";
      // If any value explicitly says Free, show without currency
      if (item.oneOffCost === 'Free' || item.subscriptionCost === 'Free' || item.serviceType === 'Free') {
        return 'Free';
      }
      // Primary logic based on declared serviceType
      if (item.serviceType === 'Daily Charge' && item.subscriptionCost) {
        return `${this.facilityCurrency} ${item.subscriptionCost}`;
      }
      if (item.serviceType === 'One-Off Charge') {
        if (item.oneOffCost === 'POA') return 'POA';
        if (item.oneOffCost) return `${this.facilityCurrency}${item.oneOffCost}`;
      }
      if (item.serviceType === 'One-Off Charge/Daily Charge') {
        const oneOff = item.oneOffCost === 'POA' ? 'POA' : `${this.facilityCurrency} ${item.oneOffCost ?? ''}`.trim();
        const daily = `${this.facilityCurrency} ${item.subscriptionCost ?? ''}`.trim();
        if (oneOff && daily) {
          return `${this.$t('ReportPremiumServices.OneOff')} ${oneOff} / ${this.$t('ReportPremiumServices.Daily')} ${this.$t('ReportPremiumServices.Charge')} ${daily}`;
        }
      }
      // Fallbacks if serviceType is missing or costs are only partially populated
      if (item.oneOffCost === 'POA') return 'POA';
      if (item.oneOffCost) return `${this.facilityCurrency}${item.oneOffCost}`;
      if (item.subscriptionCost) return `${this.facilityCurrency} ${item.subscriptionCost}`;
      return '';
    },
    truncateResidentName(name) {
      return cutString(name, 14);
    }
  }
};